import { ComponentProps, FC } from 'react';

import { DropdownOption } from '@dbt-labs/sourdough';
import { ColumnDef } from '@dbt-labs/sourdough';

import { PaginatedTable } from '@/components/PaginatedTable';
import { PageInfo } from '@/reducers/usePaginatedQuery';

export const INITIAL_PAGE_SIZE = 10;

interface ResourceFilterTableParams
  extends Pick<
    ComponentProps<typeof PaginatedTable>,
    | 'isLoading'
    | 'isSortable'
    | 'pageSize'
    | 'onChangeSort'
    | 'testId'
    | 'rowHeight'
    | 'initialSortColumn'
    | 'initialSortDesc'
  > {
  pluralizedResultName: string;
  columns: ColumnDef<any, any>[];
  data: any[] | undefined;
  pageInfo: PageInfo<any> | undefined;
  isLoadingMore: boolean;
  fetchMore: () => void;
}

const pageOptions: DropdownOption[] = [
  { label: '5 Rows', value: 5 },
  { label: '10 Rows', value: 10 },
  { label: '25 Rows', value: 25 },
  { label: '50 Rows', value: 50 },
];

export const ResourceFilterTable: FC<ResourceFilterTableParams> = ({
  pluralizedResultName,
  data,
  pageInfo,
  isLoading,
  isLoadingMore,
  isSortable = false,
  pageSize,
  columns,
  fetchMore,
  onChangeSort,
  rowHeight = 40,
  testId = 'search-result',
  ...props
}) => {
  const pageDisplayLabel =
    data?.length == null || pageInfo?.totalCount == null
      ? undefined
      : `Loaded ${data.length} of ${pageInfo.totalCount}`;
  return (
    <PaginatedTable
      {...props}
      data={data || []}
      rowHeight={rowHeight}
      isSortable={isSortable}
      testId={testId}
      loadingRowCount={pageSize}
      isLoading={isLoading}
      maxResultCount={pageInfo?.totalCount == null ? undefined : pageInfo.totalCount}
      pageSize={pageSize}
      columns={columns}
      onChangeSort={onChangeSort}
      emptyStateProps={{
        header: `No ${pluralizedResultName}`,
        helpText: `No ${pluralizedResultName} were found`,
      }}
      pagination={{
        selectedPageCount: pageOptions.find(({ value }) => value === pageSize),
        pageOptions,
        shouldShowLoadMore: !isLoadingMore && pageInfo?.hasNextPage,
        hasMorePages: pageInfo?.hasNextPage,
        onLoadMore: fetchMore,
        isPageLoading: isLoadingMore,
        pageDisplayLabel,
      }}
    />
  );
};
